"use client";

import React, { useState } from "react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";

interface Option {
  value: string;
  label: string;
}

interface propTypes {
  handleRefresh: () => void;
}

const ContactCreateModal = ({ handleRefresh }: propTypes) => {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [show, setShow] = useState(false);

  const toggle = () => setShow((prev) => !prev);

  const contactTypeOptions: Option[] = [
    { value: "lead", label: "Lead" },
    { value: "customer", label: "Customer" },
  ];

  const timezoneOptions: Option[] = Intl.supportedValuesOf("timeZone").map(
    (tz) => ({
      value: tz,
      label: tz,
    })
  );

  // Validation Schema
  const validationSchema = Yup.object().shape({
  firstName: Yup.string().nullable(),
  lastName: Yup.string().nullable(),
  
  email: Yup.string()
    .email("Invalid email")
    .nullable()
    .test(
      "email-or-phone-required",
      "Either email or phone is required",
      function (value) {
        const { phone } = this.parent;
        return !!(value || phone);
      }
    ),

  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, "Invalid phone number")
    .nullable()
    .test(
      "phone-or-email-required",
      "Either phone or email is required",
      function (value) {
        const { email } = this.parent;
        return !!(value || email);
      }
    ),

  contactType: Yup.string()
    .oneOf(["lead", "customer"], "Invalid contact type")
    .required("Please select contact type"),

  timezone: Yup.string().nullable(),
});

  // Formik setup
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      contactType: "",
      timezone: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        locationId: locationId || "",
      };
      const { response } = await AppService.contact.create({
        payload,
        toaster: true,
      });
      if (response) {
        formik.resetForm();
        toggle();
        handleRefresh();
      }
    },
  });

  return (
    <>
      <button
        type="button"
        className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 focus:ring focus:ring-custom-100"
        onClick={toggle}
      >
        <Plus className="inline-block size-4 mr-1" />
        <span className="align-middle">Add Contact</span>
      </button>

      <Modal
        show={show}
        onHide={toggle}
        id="contactModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[35rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">Add Contact</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4 max-h-[calc(100vh-180px)] overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* First Name */}
            <div>
              <label className="block mb-1 text-sm font-medium">First Name</label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter first name"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
              />
              {formik.touched.firstName && formik.errors.firstName && (
                <p className="text-red-400 text-sm mt-1">{formik.errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block mb-1 text-sm font-medium">Last Name</label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter last name"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-400 text-sm mt-1">{formik.errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium">Phone</label>
              <input
                type="text"
                name="phone"
                placeholder="Enter phone number"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-red-400 text-sm mt-1">{formik.errors.phone}</p>
              )}
            </div>

            {/* Contact Type */}
            <div>
              <label className="block mb-1 text-sm font-medium">Contact Type</label>
              <Select
                name="contactType"
                options={contactTypeOptions}
                placeholder="Select type"
                value={
                  formik.values.contactType
                    ? {
                        value: formik.values.contactType,
                        label:
                          formik.values.contactType === "lead"
                            ? "Lead"
                            : "Customer",
                      }
                    : null
                }
                onChange={(option) =>
                  formik.setFieldValue("contactType", option?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("contactType", true)}
              />
              {formik.touched.contactType && formik.errors.contactType && (
                <p className="text-red-400 text-sm mt-1">{formik.errors.contactType}</p>
              )}
            </div>

            {/* Timezone */}
            <div>
              <label className="block mb-1 text-sm font-medium">Timezone</label>
              <Select
                name="timezone"
                options={timezoneOptions}
                placeholder="Select timezone"
                value={
                  formik.values.timezone
                    ? { value: formik.values.timezone, label: formik.values.timezone }
                    : null
                }
                onChange={(option) =>
                  formik.setFieldValue("timezone", option?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("timezone", true)}
              />
              {formik.touched.timezone && formik.errors.timezone && (
                <p className="text-red-400 text-sm mt-1">{formik.errors.timezone}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              >
                Add Contact
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ContactCreateModal;
